"use client";

import { useTranslations } from "next-intl";
import Section from "../../layout/section";
import Icon from "../../shared/ui/icon";
import { DateTimeInput } from "../../forms/datetime-picker";
import { TarifFixeCourse } from "../../../types/models/tarif-fixe-course";
import { InfoTransportArretForm } from "../../../types/forms/tarif-fixe-course-form";
import { formatPrice } from "../../../types/course-estimate";
import { formatAddressSummary, getLabelAndIconFromOrdre } from "../../../utils/course-stops";
import { TYPE_AVION, TYPE_TRAIN } from "../../../constants/stop-type-const";

type CourseTarifFixeSectionProps = {
  tarifsFixes: TarifFixeCourse[];
  isLoading: boolean;
  tarifFixeSelectionne: TarifFixeCourse | null;
  onSelectionChange: (idTarifFixe: number | null) => void;
  infosTransport: InfoTransportArretForm[];
  onInfosTransportChange: (infos: InfoTransportArretForm[]) => void;
};

/**
 * Choix d'un itinéraire à tarif négocié, et récapitulatif de ce qu'il fige.
 *
 * Tant qu'un tarif est sélectionné, le trajet ne se saisit plus : il vient du
 * tarif, et le prix affiché est celui négocié — le moteur de tarif n'est pas
 * consulté. Seules les informations de vol/train restent à renseigner, parce
 * qu'elles dépendent de la date et non du trajet.
 */
export const CourseTarifFixeSection = ({
  tarifsFixes,
  isLoading,
  tarifFixeSelectionne,
  onSelectionChange,
  infosTransport,
  onInfosTransportChange,
}: CourseTarifFixeSectionProps) => {
  const t = useTranslations("tarif_fixe_section");
  const tStop = useTranslations("stop_form");

  // Rien a proposer, rien a afficher : une section vide n'apprend rien et
  // encombre un formulaire deja long.
  if (!isLoading && tarifsFixes.length === 0) return null;

  const arretsOrdonnes = tarifFixeSelectionne
    ? [...tarifFixeSelectionne.arrets].sort((a, b) => a.ordre - b.ordre)
    : [];

  const majInfo = (
    ordre: number,
    champ: keyof Omit<InfoTransportArretForm, "ordre">,
    valeur: string,
  ) => {
    const existante = infosTransport.find((info) => info.ordre === ordre) ?? {
      ordre,
      companyTransport: null,
      numeroTransport: null,
    };

    onInfosTransportChange([
      ...infosTransport.filter((info) => info.ordre !== ordre),
      { ...existante, [champ]: valeur || null },
    ]);
  };

  const infoDe = (ordre: number) => infosTransport.find((info) => info.ordre === ordre);

  return (
    <Section titre={t("title")} icon="sell">
      <div className="space-y-4">
        <div className="space-y-2">
          <label
            htmlFor="tarif-fixe"
            className="text-sm font-semibold text-slate-700 dark:text-white"
          >
            {t("select_label")}
          </label>
          <select
            id="tarif-fixe"
            disabled={isLoading}
            value={tarifFixeSelectionne?.id ?? ""}
            onChange={(event) =>
              onSelectionChange(event.target.value ? Number(event.target.value) : null)
            }
            className="w-full rounded-lg border border-slate-200 dark:border-slate-300 bg-slate-50/50 dark:bg-slate-50/10 px-4 py-3 text-sm text-slate-900 dark:text-white"
          >
            <option value="" className="bg-white text-slate-900 dark:bg-slate-800 dark:text-white">
              {t("select_none")}
            </option>
            {tarifsFixes.map((tarif) => (
              <option
                key={tarif.id}
                value={tarif.id}
                className="bg-white text-slate-900 dark:bg-slate-800 dark:text-white"
              >
                {tarif.libelle} — {formatPrice(tarif.prixFixe)}
              </option>
            ))}
          </select>
        </div>

        {tarifFixeSelectionne && (
          <div className="rounded-xl border border-primary/20 bg-primary/5 dark:bg-white/10 dark:border-white p-4 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-bold uppercase tracking-widest text-primary dark:text-white">
                  {t("negotiated_price")}
                </p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {formatPrice(tarifFixeSelectionne.prixFixe)}
                </p>
              </div>
              <Icon name="lock" className="text-primary dark:text-white" />
            </div>

            <ul className="space-y-2">
              {arretsOrdonnes.map((arret, index) => {
                const { labelOrdre, iconOrdre } = getLabelAndIconFromOrdre(
                  index,
                  arretsOrdonnes.length,
                  {
                    start: tStop("departure_label"),
                    end: tStop("destination_label"),
                    stopover: (n) => tStop("stopover_label", { n }),
                  },
                );

                return (
                  <li key={arret.ordre} className="flex items-start gap-3">
                    <Icon
                      name={iconOrdre}
                      className="text-primary dark:text-white text-lg mt-0.5"
                    />
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-300">
                        {labelOrdre}
                      </p>
                      <p className="text-sm text-slate-900 dark:text-white">
                        {arret.adresse.nom || formatAddressSummary(arret.adresse)}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Les informations de vol/train ne sont pas dans le tarif : elles
                changent à chaque réservation. */}
            {arretsOrdonnes
              .filter((arret) => arret.type === TYPE_AVION || arret.type === TYPE_TRAIN)
              .map((arret) => (
                <div
                  key={`transport-${arret.ordre}`}
                  className="border-t border-primary/20 dark:border-white/30 pt-4 space-y-3"
                >
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-300">
                    {arret.type === TYPE_AVION ? t("flight") : t("train")} —{" "}
                    {arret.adresse.nom || formatAddressSummary(arret.adresse)}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      placeholder={t("company")}
                      value={infoDe(arret.ordre)?.companyTransport ?? ""}
                      onChange={(e) => majInfo(arret.ordre, "companyTransport", e.target.value)}
                      className="rounded-lg border border-slate-200 dark:border-slate-300 bg-white dark:bg-slate-50/10 px-3 py-2 text-sm text-slate-900 dark:text-white"
                    />
                    <input
                      placeholder={t("number")}
                      value={infoDe(arret.ordre)?.numeroTransport ?? ""}
                      onChange={(e) => majInfo(arret.ordre, "numeroTransport", e.target.value)}
                      className="rounded-lg border border-slate-200 dark:border-slate-300 bg-white dark:bg-slate-50/10 px-3 py-2 text-sm text-slate-900 dark:text-white"
                    />
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </Section>
  );
};
